import { useCallback, useEffect, useRef } from "react";

const NEAR_BOTTOM_PX = 80;

/**
 * Keeps a scroll container pinned to the bottom while its content grows
 * (streaming text), unless the user has scrolled up to read. Starts at the
 * bottom.
 */
export function useStickToBottom<T extends HTMLElement, C extends HTMLElement>() {
  const scrollRef = useRef<T>(null);
  const contentRef = useRef<C>(null);
  const pinned = useRef(true);

  useEffect(() => {
    const scroller = scrollRef.current;
    const content = contentRef.current;
    if (!scroller || !content) return;
    let lastTop = scroller.scrollTop;
    const onScroll = () => {
      const top = scroller.scrollTop;
      const nearBottom = scroller.scrollHeight - top - scroller.clientHeight < NEAR_BOTTOM_PX;
      // Only scrolling up can let go. Our own jump to the bottom fires its scroll event a frame
      // later; if more content arrived in between (a reply settling, document cards loading),
      // that event sees a gap below, but the reader didn't move, so stay pinned.
      if (top < lastTop) pinned.current = nearBottom;
      else if (nearBottom) pinned.current = true;
      lastTop = top;
    };
    const stick = () => {
      if (pinned.current) {
        scroller.scrollTop = scroller.scrollHeight;
        lastTop = scroller.scrollTop;
      }
    };
    stick();
    scroller.addEventListener("scroll", onScroll, { passive: true });
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(stick) : null;
    observer?.observe(content);
    return () => {
      scroller.removeEventListener("scroll", onScroll);
      observer?.disconnect();
    };
  }, []);

  /** Jump to the bottom and re-pin (e.g. after the user sends). */
  const toBottom = useCallback(() => {
    pinned.current = true;
    const scroller = scrollRef.current;
    if (scroller) scroller.scrollTop = scroller.scrollHeight;
  }, []);

  return { scrollRef, contentRef, toBottom };
}
